import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Badge, Button, ButtonGroup, Card, CardBody, CardHeader, CardTitle, Input, Spinner } from 'reactstrap';
import { ArrowDown, ArrowUp, Edit, Menu, Plus, Search, Trash } from 'react-feather';
import toast from 'react-hot-toast';
import ReorderPlaylistComponent from '@/v2/components/Playlist/ReorderPlaylistComponent';
import DeleteModalPlayList from '@/component/playlist/personal-playlist/DeleteModalPlayList';
import { featuredPlaylistAdminApi, getApiErrorMessage } from '@/data/api/featuredPlaylistAdminApi';
import { formatReleaseET, isReleased } from './releaseTime';
import './FeaturedPlaylists.css';

const STATUS_FILTERS = [
	{ key: 'all', label: 'All' },
	{ key: 'live', label: 'Live' },
	{ key: 'scheduled', label: 'Scheduled' }
];

export const ReleaseBadge = ({ releaseDateTime }) =>
	isReleased(releaseDateTime) ? (
		<Badge color="success" pill>
			Live
		</Badge>
	) : (
		<Badge color="warning" pill className="text-dark" title="Hidden from members until this time">
			Scheduled · {formatReleaseET(releaseDateTime)}
		</Badge>
	);

function FeaturedPlaylistManager() {
	const navigate = useNavigate();
	const [playlists, setPlaylists] = useState([]);
	const [savedOrderIds, setSavedOrderIds] = useState([]);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState('');
	const [search, setSearch] = useState('');
	const [debouncedSearch, setDebouncedSearch] = useState('');
	const [statusFilter, setStatusFilter] = useState('all');
	const [savingOrder, setSavingOrder] = useState(false);
	const [deleteTarget, setDeleteTarget] = useState(null);

	useEffect(() => {
		const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
		return () => clearTimeout(t);
	}, [search]);

	const load = useCallback(async q => {
		setLoading(true);
		setLoadError('');
		try {
			const data = await featuredPlaylistAdminApi.list(q);
			setPlaylists(data);
			if (!q) setSavedOrderIds(data.map(p => p._id));
		} catch (error) {
			setLoadError(getApiErrorMessage(error, 'Could not load featured playlists'));
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		load(debouncedSearch);
	}, [debouncedSearch, load]);

	const visible = useMemo(() => {
		if (statusFilter === 'all') return playlists;
		return playlists.filter(p => (statusFilter === 'live' ? isReleased(p.releaseDateTime) : !isReleased(p.releaseDateTime)));
	}, [playlists, statusFilter]);

	// Reordering only makes sense on the complete, unfiltered list.
	const canReorder = !debouncedSearch && statusFilter === 'all' && !loading;
	const orderDirty = canReorder && playlists.map(p => p._id).join() !== savedOrderIds.join();

	const move = (from, to) => {
		if (to < 0 || to >= playlists.length) return;
		const next = [...playlists];
		const [item] = next.splice(from, 1);
		next.splice(to, 0, item);
		setPlaylists(next);
	};

	const saveOrder = async () => {
		setSavingOrder(true);
		try {
			const ids = playlists.map(p => p._id);
			await featuredPlaylistAdminApi.reorder(ids);
			setSavedOrderIds(ids);
			toast.success('Playlist order saved', { position: 'top-center' });
		} catch (error) {
			toast.error(getApiErrorMessage(error, 'Could not save order'), { position: 'top-center' });
		} finally {
			setSavingOrder(false);
		}
	};

	const discardOrder = () => {
		const byId = new Map(playlists.map(p => [p._id, p]));
		setPlaylists(savedOrderIds.map(id => byId.get(id)).filter(Boolean));
	};

	const confirmDelete = async () => {
		const target = deleteTarget;
		setDeleteTarget(null);
		if (!target) return;
		try {
			await featuredPlaylistAdminApi.remove(target._id);
			setPlaylists(prev => prev.filter(p => p._id !== target._id));
			setSavedOrderIds(prev => prev.filter(id => id !== target._id));
			toast.success(`Deleted "${target.name}"`, { position: 'top-center' });
		} catch (error) {
			toast.error(getApiErrorMessage(error, 'Could not delete playlist'), { position: 'top-center' });
		}
	};

	const renderRow = (playlist, index) => (
		<div className="fpl-row" key={playlist._id}>
			{canReorder && (
				<span className="fpl-drag" title="Drag to reorder">
					<Menu size={16} />
				</span>
			)}
			<span className="fpl-position">{index + 1}</span>
			{playlist.thumbnailLink ? (
				<img className="fpl-thumb" src={playlist.thumbnailLink} alt="" loading="lazy" />
			) : (
				<div className="fpl-thumb fpl-thumb--empty">No image</div>
			)}
			<div className="fpl-main">
				<div className="fpl-name">{playlist.name}</div>
				{playlist.description && <div className="fpl-desc">{playlist.description}</div>}
				<div className="fpl-meta">
					<ReleaseBadge releaseDateTime={playlist.releaseDateTime} />
					<span>
						{playlist.videoCount} video{playlist.videoCount === 1 ? '' : 's'}
					</span>
				</div>
			</div>
			<div className="fpl-actions">
				{canReorder && (
					<ButtonGroup size="sm">
						<Button outline color="secondary" disabled={index === 0} onClick={() => move(index, index - 1)} title="Move up">
							<ArrowUp size={14} />
						</Button>
						<Button
							outline
							color="secondary"
							disabled={index === playlists.length - 1}
							onClick={() => move(index, index + 1)}
							title="Move down"
						>
							<ArrowDown size={14} />
						</Button>
					</ButtonGroup>
				)}
				<Button size="sm" color="primary" outline onClick={() => navigate(`/AdminDashboard/featured-playlist/${playlist._id}/edit`)}>
					<Edit size={14} className="me-1" />
					Edit
				</Button>
				<Button size="sm" color="danger" outline onClick={() => setDeleteTarget(playlist)} title="Delete">
					<Trash size={14} />
				</Button>
			</div>
		</div>
	);

	return (
		<>
			<Card className="fpl-card">
				<CardHeader className="d-flex flex-wrap align-items-center justify-content-between gap-2">
					<div>
						<CardTitle tag="h4" className="mb-0">
							Featured Playlists
						</CardTitle>
						<small className="text-muted">
							The order here is the order members see. Scheduled playlists stay hidden until their release time.
						</small>
					</div>
					<Button color="primary" onClick={() => navigate('/AdminDashboard/featured-playlist/new')}>
						<Plus size={16} className="me-1" />
						New playlist
					</Button>
				</CardHeader>
				<CardBody>
					<div className="fpl-toolbar">
						<div className="fpl-search">
							<Search size={16} />
							<Input
								type="search"
								placeholder="Search by playlist name, description or video title"
								value={search}
								onChange={e => setSearch(e.target.value)}
							/>
						</div>
						<ButtonGroup size="sm">
							{STATUS_FILTERS.map(f => (
								<Button
									key={f.key}
									color="secondary"
									outline={statusFilter !== f.key}
									onClick={() => setStatusFilter(f.key)}
								>
									{f.label}
								</Button>
							))}
						</ButtonGroup>
					</div>

					{!canReorder && !loading && playlists.length > 0 && (
						<Alert color="info" className="py-2 small">
							Clear the search and set the filter to "All" to reorder playlists.
						</Alert>
					)}

					{orderDirty && (
						<Alert color="warning" className="py-2 d-flex flex-wrap align-items-center justify-content-between gap-2">
							<span>You have unsaved order changes.</span>
							<span className="d-flex gap-2">
								<Button size="sm" color="secondary" outline onClick={discardOrder} disabled={savingOrder}>
									Discard
								</Button>
								<Button size="sm" color="primary" onClick={saveOrder} disabled={savingOrder}>
									{savingOrder ? <Spinner size="sm" /> : 'Save order'}
								</Button>
							</span>
						</Alert>
					)}

					{loadError && <Alert color="danger">{loadError}</Alert>}

					{loading ? (
						<div className="text-center py-5">
							<Spinner />
						</div>
					) : visible.length === 0 ? (
						<div className="text-center text-muted py-5">
							{debouncedSearch || statusFilter !== 'all' ? 'No playlists match.' : 'No featured playlists yet.'}
						</div>
					) : canReorder ? (
						<ReorderPlaylistComponent
							items={playlists.map((p, i) => ({ ...p, id: p._id, position: i }))}
							onReorder={next => setPlaylists(next)}
							renderItem={(item, index) => renderRow(item, index)}
							gap={8}
						/>
					) : (
						<div className="d-flex flex-column gap-2">{visible.map((p, i) => renderRow(p, i))}</div>
					)}
				</CardBody>
			</Card>

			<DeleteModalPlayList
				modal={Boolean(deleteTarget)}
				onConfirmAction={confirmDelete}
				onCancelAction={() => setDeleteTarget(null)}
			/>
		</>
	);
}

export default FeaturedPlaylistManager;
